import { Injectable, ViewContainerRef } from '@angular/core';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import type { Observable } from 'rxjs';
import { asapScheduler, combineLatest, debounceTime, map, of, switchMap, tap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { UserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import { FeatureGuardFacade, GuardedFeature } from '@core/feature-guard-legacy';
import { RoleSelectors } from '@core/roles-data';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import type { Visibility } from '@core/visibility-types';
import { VisibilityLevel } from '@core/visibility-types';

import { VisibilitySelectorAnalytics } from './visibility-selector.analytics';
import type { VisibilityDialogData } from '../visibility-dialog/visibility-dialog.component';
import { VisibilityDialogComponent } from '../visibility-dialog/visibility-dialog.component';

@Injectable()
export class VisibilitySelectorService {
  public featureVisibility$ = this.featureGuardFacade.checkFeatureAvailability(
    GuardedFeature.Visibility,
  );

  private usersData$ = combineLatest([
    this.store.select(UserSelectors.entities()).pipe(filterBoolean),
    this.store.select(RoleSelectors.entities()).pipe(filterBoolean),
  ]).pipe(
    debounceTime(0, asapScheduler),
    distinctUntilChanged(deepEqual),
    map(([admins, roles]) => {
      const groupedAdmins = admins.reduce<Record<string, User[]>>(
        (groups, admin) => ({
          ...groups,
          ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
        }),
        {},
      );

      return {
        rolesWithAdmins: roles.map(r => ({
          ...r,
          id: r.id!,
          isAdmin: r.admin,
          admins: groupedAdmins[r.id!] || [],
          count: (groupedAdmins[r.id!] && groupedAdmins[r.id!].length) || 0,
        })),
        admins,
      };
    }),
  );

  constructor(
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    private analytics: VisibilitySelectorAnalytics,
    private store: Store,
    private featureGuardFacade: FeatureGuardFacade,
  ) {}

  public onVisibilityChanges(
    level: VisibilityLevel,
    currentVisibility: Visibility,
    options: Omit<VisibilityDialogData, 'assignedRoles' | 'assignedAdmins'>,
  ): Observable<Visibility | undefined> {
    if (level === VisibilityLevel.Protected) {
      return this.dialogService
        .openCustom(
          VisibilityDialogComponent,
          { width: DialogWidth.SmallX },
          {
            assignedAdmins: currentVisibility.adminIds,
            assignedRoles: currentVisibility.roleIds,
            ...options,
          },
          this.vcRef,
        )
        .afterClosed.pipe(
          tap(value => {
            if (!value) return;

            this.analytics.trackChange({ level, selectedMembersCount: value.adminIds.length });
          }),
        );
    } else {
      if (currentVisibility.level !== level) {
        this.analytics.trackChange({ level, selectedMembersCount: 0 });
      }

      return of({ roleIds: [], level, adminIds: [] });
    }
  }

  public getVisibilityUsersData(visibility$: Observable<Visibility>) {
    return this.usersData$.pipe(
      switchMap(({ admins, rolesWithAdmins }) =>
        visibility$.pipe(
          map(visibility => ({
            admins: admins.filter(a => visibility.adminIds.includes(a.id)),
            rolesWithAdmins: rolesWithAdmins.filter(r => visibility.roleIds.includes(r.id)),
          })),
        ),
      ),
    );
  }
}
